import {
  ServerMessage,
  ClientMessage,
  LoginMessage,
  SelectCharMessage,
  MoveMessage,
  ChatMessage,
  AttackMessage,
  UseItemMessage,
  DropItemRequestMessage,
  PickupItemMessage,
  NpcTalkMessage,
  NpcActionMessage,
  ShopResultMessage,
  ShopResultItem,
  ShopResultType,
  ShopSellListPayload,
  ShopBuyListPayload,
  RetrieveListPayload,
  TeleportAckMessage,
  CharInfo,
  CharSpawnPayload,
  ItemData,
  TeleportPayload,
  ItemAmountPayload,
  ShowHtmlPayload,
  CharVisualUpdatePayload,
  LawfulPayload,
  ParalysisPayload,
  AttackResultPayload,
  PoisonPayload,
  SpMrPayload,
  StatUpPayload,
  CharTitlePayload,
  DoorStatusPayload,
  SkillSoundPayload,
  PolyPayload,
  InvisPayload,
  LightPayload,
  AttrDefPayload,
  ChangeHeadingPayload,
  BookmarkEntry,
  IdentifyDescPayload,
  MpUpdatePayload,
  HpUpdatePayload,
  ServerChatPayload,
  ServerChatChannel,
} from '../types/network';
import { CHAT_TYPE } from './Protocol';

/**
 * 서버 `S_ChatPacket`의 type 값 → 채널명.
 *
 * 게이트웨이가 opcode 10(전체/혈맹/파티/트레이드/연합/위스퍼)에도 채널을 붙여 보내지만,
 * 아직 모르는 type이면 `"unknown_N"`이 온다. 그때 이 표로 되살린다.
 * 출처: `src/jp/l1j/server/packets/server/S_ChatPacket.java` 생성자의 type 분기.
 */
const CHAT_CHANNEL_BY_TYPE: Record<number, ServerChatChannel> = {
  0: 'normal',      // 通常チャット
  2: 'shout',       // 叫び
  3: 'world',       // 全体チャット
  4: 'clan',        // 血盟チャット
  9: 'whisper',     // ウィスパー(발신 에코)
  11: 'party',      // パーティーチャット
  12: 'trade',      // トレードチャット
  13: 'alliance',   // 連合チャット
  14: 'chat_party', // チャットパーティー
  16: 'whisper',    // ウィスパー(수신, opcode 255)
};

/**
 * server_chat payload의 채널명을 확정한다.
 *
 * 게이트웨이가 붙여 준 channel을 우선하되, 비었거나 `"unknown_N"`이면
 * 원본 chatType으로 되살린다. 그래도 모르면 'normal'로 떨어뜨린다.
 */
export function serverChatChannelName(
  payload: { channel?: string | null; chatType?: number }
): ServerChatChannel {
  const channel = payload?.channel;
  if (channel && !channel.startsWith('unknown_')) return channel;
  if (typeof payload?.chatType === 'number') {
    return CHAT_CHANNEL_BY_TYPE[payload.chatType] ?? 'normal';
  }
  return 'normal';
}

/**
 * sendUseItem의 선택 인자 — 아이템 use_type에 따라 서버가 추가로 읽는 필드들.
 * (UseItemMessage.payload에서 itemId를 뺀 나머지)
 */
export type UseItemOptions = Omit<UseItemMessage['payload'], 'itemId'>;

/**
 * PacketHandler - Maps between game actions and Gateway messages.
 *
 * Responsibilities:
 * - Encode game actions into Gateway JSON messages
 * - Decode Gateway JSON messages into game events
 * - Maintain character list cache
 * - Track player spawn data
 */
export class PacketHandler {
  // Character list cache
  private charList: CharInfo[] = [];
  private charCount: number = 0;
  /**
   * 이번 세션의 캐릭터 목록이 확정됐는지.
   *
   * 캐릭터가 없는 계정은 목록이 빈 배열인데, 그것만으로는 "아직 안 받았다"와
   * 구별할 수 없다(둘 다 length 0). char_count는 로그인 직후 곧바로 오므로
   * CharSelectScene이 리스너를 걸기 전에 지나가기 쉬워, 씬이 캐시를 되짚을 때
   * 이 플래그가 없으면 영원히 로딩에 머문다.
   */
  private charListResolved: boolean = false;

  /** char_count 미수신 상태에서 char_list가 도착했을 때의 안전망 타이머. */
  private charListFallbackTimer: number | null = null;

  /** 안전망 타이머 지연 (ms) — 이 시간 안에 char_count가 오지 않으면 누적분을 emit. */
  private static readonly CHAR_LIST_FALLBACK_MS = 1000;

  // Player spawn data
  private playerSpawn: CharSpawnPayload | null = null;

  /** 마지막으로 받은 순간이동 북마크 목록 (S_BookmarkLoad). */
  private bookmarks: BookmarkEntry[] = [];

  /**
   * 마지막으로 알던 최대 HP/MP.
   * hp_update/mp_update가 최대치를 싣지 않고 올 수 있어(구버전 게이트웨이의
   * 정수 mp_update 등) 직전 값을 이어서 실어 보낸다.
   */
  private lastMaxHp: number = 0;
  private lastMaxMp: number = 0;

  // Event listeners for game events
  private gameListeners: Map<string, Set<(data: unknown) => void>> = new Map();

  constructor(private sendFn: (msg: ClientMessage) => void) {}

  // ========== Send Messages to Gateway ==========

  /**
   * Send login request.
   */
  sendLogin(account: string, password: string): void {
    const msg: LoginMessage = {
      type: 'login',
      payload: { account, password },
    };
    this.sendFn(msg);
  }

  /**
   * Send character selection.
   */
  sendSelectChar(charName: string): void {
    const msg: SelectCharMessage = {
      type: 'select_char',
      payload: { charName },
    };
    this.sendFn(msg);
  }

  /**
   * Send move packet.
   */
  sendMove(x: number, y: number, heading: number): void {
    const msg: MoveMessage = {
      type: 'move',
      payload: { x, y, heading },
    };
    this.sendFn(msg);
  }

  /**
   * Send chat message.
   */
  sendChat(message: string, channel: string = 'normal'): void {
    const msg: ChatMessage = {
      type: 'chat',
      payload: { message, channel },
    };
    this.sendFn(msg);
  }

  /**
   * Send attack packet.
   *
   * @param x 대상의 타일 좌표 X. 서버가 사거리를 판정할 때 쓴다.
   * @param y 대상의 타일 좌표 Y.
   */
  sendAttack(targetId: number, x: number, y: number): void {
    const msg: AttackMessage = {
      type: 'attack',
      payload: { targetId, x, y },
    };
    this.sendFn(msg);
  }

  /**
   * Send use item packet.
   *
   * 아이템의 use_type에 따라 서버 C_UseItem이 objId 뒤에서 추가 필드를 읽는다.
   * 아는 값이 있으면 options로 넘긴다 (targetId / mapId / x,y / skillId / text / useType).
   * 생략하면 게이트웨이가 아이템 종류를 보고 채운다.
   */
  sendUseItem(itemId: number, options?: UseItemOptions): void {
    const msg: UseItemMessage = {
      type: 'use_item',
      payload: { itemId, ...(options ?? {}) },
    };
    this.sendFn(msg);
  }

  /**
   * Send drop item packet (서버 `C_DropItem`, opcode 54).
   *
   * **좌표가 착지 지점이고 서버는 그 값을 검증하지 않는다.** 플레이어에게서 아무리
   * 멀어도 그 타일에 놓이므로, 사거리 제한은 호출하는 쪽에서 지켜야 한다
   * (원본 클라이언트와 마찬가지다).
   *
   * 성공 여부는 응답으로 알 수 있다 — 인벤토리 쪽은 `delete_item`/`item_amount`,
   * 바닥 쪽은 `drop_item`(S_DropItem)이 온다. 거절되면 `server_message`만 온다.
   *
   * @param itemId 버릴 인벤토리 아이템의 objId
   * @param count  버릴 개수 (겹치지 않는 아이템은 1)
   * @param x      아이템이 놓일 타일 좌표
   * @param y      아이템이 놓일 타일 좌표
   */
  sendDropItem(itemId: number, count: number, x: number, y: number): void {
    const msg: DropItemRequestMessage = {
      type: 'drop_item',
      payload: { itemId, count, x, y },
    };
    this.sendFn(msg);
  }

  /**
   * Send pick up item packet (서버 `C_PickUpItem`, opcode 188).
   *
   * **x/y는 플레이어 위치가 아니라 아이템이 놓인 타일이다.** 서버가 그 좌표의 지면
   * 인벤토리에서 objId를 찾으므로 좌표가 틀리면 아무 일도 일어나지 않는다.
   * 서버는 3타일을 넘으면 응답 없이 무시하므로 호출 전에 거리를 확인할 것.
   *
   * @param objId 바닥 아이템의 objId
   * @param count 주울 개수 (겹치는 아이템만 의미가 있다)
   * @param x     아이템이 놓인 타일 좌표
   * @param y     아이템이 놓인 타일 좌표
   */
  sendPickupItem(objId: number, count: number, x: number, y: number): void {
    const msg: PickupItemMessage = {
      type: 'pickup_item',
      payload: { objId, count, x, y },
    };
    this.sendFn(msg);
  }

  /**
   * Send NPC talk request.
   *
   * 서버 `C_NpcTalk`(opcode 58)는 objId 하나만 읽는다. 대화 상대라면
   * `show_html`(S_NpcTalkReturn)이 돌아오고, 아니면 아무 응답도 없다.
   */
  sendNpcTalk(objId: number): void {
    const msg: NpcTalkMessage = {
      type: 'npc_talk',
      payload: { objId },
    };
    this.sendFn(msg);
  }

  /**
   * Send NPC action (dialog choice).
   *
   * 서버 `C_NpcAction`(opcode 37)은 objId + 액션 문자열을 읽는다. 문자열은
   * `show_html`로 받은 대화창의 링크 이름이다.
   *
   * **`"buy"`/`"sell"`은 NPC 관점의 이름이다** — `"buy"`를 보내면 NPC가 파는
   * 목록(= 플레이어의 구매 목록)이 `shop_sell_list`로, `"sell"`을 보내면 NPC가
   * 사주는 값(= 플레이어의 판매 목록)이 `shop_buy_list`로 돌아온다.
   */
  sendNpcAction(objId: number, action: string): void {
    const msg: NpcActionMessage = {
      type: 'npc_action',
      payload: { objId, action },
    };
    this.sendFn(msg);
  }

  /**
   * Send shop trade confirmation.
   *
   * 서버 `C_Result`(opcode 40)는 항목마다 `id + count`를 읽는데 **id의 정체가
   * resultType마다 다르다** — 0(구매)이면 `shop_sell_list`의 `index`,
   * 1(판매)·2(창고)면 인벤토리 아이템의 objId다.
   *
   * 서버는 NPC와 3타일을 넘게 떨어져 있거나 items가 비면 조용히 무시한다.
   *
   * @param resultType 0=구매 / 1=판매 / 2=창고 보관
   */
  sendShopResult(npcObjId: number, resultType: ShopResultType, items: ShopResultItem[]): void {
    const msg: ShopResultMessage = {
      type: 'shop_result',
      payload: { npcObjId, resultType, items },
    };
    this.sendFn(msg);
  }

  /**
   * Send teleport acknowledgement.
   *
   * S_Teleport(opcode 4)를 받은 뒤 화면 정리가 끝나면 보낸다.
   * 이 응답이 없으면 서버는 텔레포트 대기 상태에서 좌표를 옮기지 않는다.
   */
  sendTeleportAck(): void {
    const msg: TeleportAckMessage = { type: 'teleport_ack' };
    this.sendFn(msg);
  }

  /**
   * Send ping (handled automatically by ConnectionManager).
   */
  sendPing(): void {
    this.sendFn({ type: 'ping' });
  }

  // ========== Handle Server Messages ==========

  /**
   * Process incoming server message and emit game events.
   */
  handleServerMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'connected':
        this.handleConnected(message);
        break;

      case 'login_result':
        this.handleLoginResult(message);
        break;

      case 'char_count':
        this.handleCharCount(message);
        break;

      case 'char_list':
        this.handleCharList(message);
        break;

      // ⚠ 게이트웨이는 char_spawn을 보내지 않는다 (실제로는 opcode 3 → 'spawn').
      // WebMessage.charSpawn() 빌더만 남아 있고 ProtocolTranslator가 호출하지 않아
      // 이 case는 실행되지 않는다. CharSelectScene이 아직 구독 중이라 지우지 않는다.
      case 'char_spawn':
        this.handleCharSpawn(message);
        break;

      case 'object_remove':
        this.handleObjectRemove(message);
        break;

      case 'inventory':
        this.handleInventory(message);
        break;

      // ⚠ 게이트웨이는 chat_message를 보내지 않는다 (실제로는 'server_chat').
      // WebMessage.chatMessage() 빌더만 남아 있고 ProtocolTranslator가 호출하지 않아
      // 이 case는 실행되지 않는다. 기존 구독을 흔들지 않도록 남겨 둔다.
      case 'chat_message':
        this.handleChatMessage(message);
        break;

      case 'server_chat':
        this.handleServerChat(message);
        break;

      case 'sys_message':
        this.handleSysMessage(message);
        break;

      case 'hp_update':
        this.handleHpUpdate(message);
        break;

      case 'mp_update':
        this.handleMpUpdate(message);
        break;

      case 'bookmarks':
        this.handleBookmarks(message);
        break;

      case 'identify_desc':
        this.handleIdentifyDesc(message);
        break;

      case 'pong':
        this.handlePong(message);
        break;

      case 'error':
        this.handleError(message);
        break;

      case 'teleport':
        this.handleTeleport(message);
        break;

      case 'item_amount':
        this.handleItemAmount(message);
        break;

      case 'show_html':
        this.handleShowHtml(message);
        break;

      case 'char_visual_update':
        this.handleCharVisualUpdate(message);
        break;

      case 'lawful':
        this.handleLawful(message);
        break;

      case 'paralysis':
        this.handleParalysis(message);
        break;

      case 'skill_sound':
        this.handleSkillSound(message);
        break;

      case 'poly':
        this.handlePoly(message);
        break;

      case 'invis':
        this.handleInvis(message);
        break;

      case 'light':
        this.handleLight(message);
        break;

      case 'attr_def':
        this.handleAttrDef(message);
        break;

      case 'change_heading':
        this.handleChangeHeading(message);
        break;

      case 'shop_sell_list':
        this.handleShopSellList(message);
        break;

      case 'shop_buy_list':
        this.handleShopBuyList(message);
        break;

      case 'retrieve_list':
        this.handleRetrieveList(message);
        break;

      case 'attack_result':
        this.handleAttackResult(message);
        break;

      case 'poison':
        this.handlePoison(message);
        break;

      case 'sp_mr':
        this.handleSpMr(message);
        break;

      case 'stat_up':
        this.handleStatUp(message);
        break;

      case 'char_title':
        this.handleCharTitle(message);
        break;

      case 'door_status':
        this.handleDoorStatus(message);
        break;

      case 'common_news':
        this.handleCommonNews(message);
        break;

      case 'weather':
        this.handleWeather(message);
        break;
    }
  }

  // ========== Message Handlers ==========

  private handleConnected(msg: { type: 'connected'; payload: { message: string } }): void {
    console.log('PacketHandler: Connected to Gateway');
    this.emit('gateway:connected', msg.payload);
  }

  private handleLoginResult(
    msg: { type: 'login_result'; payload: { result: number; message: string } }
  ): void {
    console.log('PacketHandler: Login result', msg.payload.result, msg.payload.message);

    // 새 로그인 세션 시작 — 이전 세션의 캐릭터 목록/카운트/스폰 캐시를 초기화해
    // 재로그인 시 이전 목록과 뒤섞이는 것을 방지한다.
    this.clearCache();

    if (msg.payload.result === 0) {
      this.emit('login:success', { message: msg.payload.message });
    } else {
      this.emit('login:failed', {
        result: msg.payload.result,
        message: msg.payload.message,
      });
    }
  }

  private handleCharCount(msg: { type: 'char_count'; payload: number }): void {
    this.charCount = msg.payload;
    console.log('PacketHandler: Character count', this.charCount);
    this.emit('char:count', this.charCount);

    if (this.charCount === 0) {
      // 캐릭터가 없는 계정 — char_list 패킷이 오지 않으므로 즉시 빈 목록 emit.
      this.emitCharList();
    } else if (this.charList.length >= this.charCount) {
      // char_list가 char_count보다 먼저 모두 도착한 경우 — 지금 emit.
      this.emitCharList();
    }
  }

  private handleCharList(msg: { type: 'char_list'; payload: CharInfo[] }): void {
    // 게이트웨이는 서버가 보낸 캐릭터 1개당 singleton list로 한 번씩 보낸다.
    // 따라서 char_count를 먼저 받은 뒤 누적한다 (총 도달 시점에 이벤트를 1회만 emit).
    for (const char of msg.payload) {
      // 동일 이름은 중복 누적 방지
      if (!this.charList.some((c) => c.name === char.name)) {
        this.charList.push(char);
      }
    }
    console.log(
      'PacketHandler: Character list progress',
      this.charList.length,
      '/',
      this.charCount
    );

    if (this.charCount > 0 && this.charList.length >= this.charCount) {
      // char_count가 확정된 뒤 마지막 패킷을 받을 때 한 번만 emit.
      this.emitCharList();
    } else {
      // char_count 미수신(=0) 또는 아직 미완성 — 부분 목록을 여러 번 emit하지 않도록
      // 보류하되, count/후속 패킷이 끝내 오지 않는 경우를 대비한 안전망 타이머를 건다.
      // (char_list가 올 때마다 타이머를 리셋해 마지막 패킷 기준으로 대기)
      this.scheduleCharListFallback();
    }
  }

  /**
   * 누적된 캐릭터 목록을 1회 emit하고 안전망 타이머를 해제한다.
   */
  private emitCharList(): void {
    this.cancelCharListFallback();
    this.charListResolved = true;
    this.emit('char:list', [...this.charList]);
  }

  private scheduleCharListFallback(): void {
    this.cancelCharListFallback();
    this.charListFallbackTimer = window.setTimeout(() => {
      this.charListFallbackTimer = null;
      console.warn(
        'PacketHandler: char_count not settled, emitting accumulated char list',
        this.charList.length,
        '/',
        this.charCount
      );
      this.emitCharList();
    }, PacketHandler.CHAR_LIST_FALLBACK_MS);
  }

  private cancelCharListFallback(): void {
    if (this.charListFallbackTimer !== null) {
      window.clearTimeout(this.charListFallbackTimer);
      this.charListFallbackTimer = null;
    }
  }

  private handleCharSpawn(
    msg: { type: 'char_spawn'; payload: CharSpawnPayload }
  ): void {
    this.playerSpawn = msg.payload;
    console.log('PacketHandler: Char spawn', msg.payload);
    this.emit('char:spawn', msg.payload);

    // Also emit player spawn specifically
    if (msg.payload.objId > 0) {
      this.emit('player:spawn', msg.payload);
    }
  }

  private handleObjectRemove(
    msg: { type: 'object_remove'; payload: { objId: number } }
  ): void {
    console.log('PacketHandler: Object remove', msg.payload.objId);
    this.emit('entity:remove', msg.payload.objId);
  }

  private handleInventory(msg: { type: 'inventory'; payload: ItemData[] }): void {
    console.log('PacketHandler: Inventory update', msg.payload.length, 'items');
    this.emit('inventory:update', msg.payload);
  }

  private handleChatMessage(
    msg: { type: 'chat_message'; payload: { type: string; sender: string; message: string } }
  ): void {
    console.log(`PacketHandler: [${msg.payload.type}] ${msg.payload.sender}: ${msg.payload.message}`);
    this.emit('chat:message', msg.payload);
  }

  /**
   * S_ChatPacket (opcode 76 / 255 / 10) — 서버가 브로드캐스트한 채팅.
   *
   * 게이트웨이가 opcode 10의 전체/혈맹/파티/트레이드/연합/위스퍼도 채널을 붙여
   * 이 타입으로 보낸다. 채널이 비었거나 `"unknown_N"`이면 원본 chatType으로 되살린다.
   *
   * **본문에 발신자를 다시 붙이지 않는다** — 서버 `S_ChatPacket`이 이미
   * `"이름: 내용"` 같은 완성된 문자열을 만들어 보내기 때문이다.
   */
  private handleServerChat(msg: { type: 'server_chat'; payload: ServerChatPayload }): void {
    const channel = serverChatChannelName(msg.payload);
    const payload: ServerChatPayload = { ...msg.payload, channel };
    console.log(`PacketHandler: [${channel}] ${payload.message}`);
    this.emit('chat:server', payload);
  }

  private handleSysMessage(
    msg: { type: 'sys_message'; payload: { message: string } }
  ): void {
    console.log('PacketHandler: System message', msg.payload.message);
    this.emit('sys:message', msg.payload.message);

    // Also emit as chat message for unified handling
    this.emit('chat:message', {
      type: 'system',
      sender: 'System',
      message: msg.payload.message,
    });
  }

  private handlePong(_msg: { type: 'pong'; payload: null }): void {
    this.emit('pong', null);
  }

  private handleError(msg: { type: 'error'; payload: { message: string } }): void {
    console.error('PacketHandler: Error from Gateway', msg.payload.message);
    this.emit('error', msg.payload);
  }

  /**
   * S_Teleport (opcode 4) — 텔레포트 시작.
   * 씬이 화면을 정리한 뒤 sendTeleportAck()를 호출해야 이동이 완료된다.
   */
  private handleTeleport(msg: { type: 'teleport'; payload: TeleportPayload }): void {
    console.log('PacketHandler: Teleport', msg.payload.objId);
    this.emit('teleport', msg.payload);
  }

  /** S_ItemAmount (opcode 127) — 인벤토리 단건의 개수/표시명 갱신. */
  private handleItemAmount(msg: { type: 'item_amount'; payload: ItemAmountPayload }): void {
    console.log('PacketHandler: Item amount', msg.payload.objId, msg.payload.count);
    this.emit('item_amount', msg.payload);
  }

  /** S_NpcTalkReturn (opcode 119) — NPC 대화 HTML 표시. */
  private handleShowHtml(msg: { type: 'show_html'; payload: ShowHtmlPayload }): void {
    console.log('PacketHandler: Show html', msg.payload.htmlId, msg.payload.args);
    this.emit('show_html', msg.payload);
  }

  /** S_CharVisualUpdate (opcode 113) — 장착 무기 등 외형 갱신. */
  private handleCharVisualUpdate(
    msg: { type: 'char_visual_update'; payload: CharVisualUpdatePayload }
  ): void {
    console.log('PacketHandler: Char visual update', msg.payload.objId, msg.payload.weapon);
    this.emit('char_visual_update', msg.payload);
  }

  /** S_Lawful (opcode 140) — 성향치 갱신. */
  private handleLawful(msg: { type: 'lawful'; payload: LawfulPayload }): void {
    console.log('PacketHandler: Lawful', msg.payload.objId, msg.payload.lawful);
    this.emit('lawful', msg.payload);
  }

  /** S_Paralysis (opcode 165) — 마비/수면/빙결/스턴/속박 및 텔레포트 대기 해제. */
  private handleParalysis(msg: { type: 'paralysis'; payload: ParalysisPayload }): void {
    console.log('PacketHandler: Paralysis kind', msg.payload.kind);
    this.emit('paralysis', msg.payload);
  }

  /** S_SkillSound (opcode 232) — 스킬 이펙트 재생. */
  private handleSkillSound(msg: { type: 'skill_sound'; payload: SkillSoundPayload }): void {
    this.emit('skill_sound', msg.payload);
  }

  /** S_ChangeShape (opcode 164) — 변신. polyId 0은 변신 해제다. */
  private handlePoly(msg: { type: 'poly'; payload: PolyPayload }): void {
    console.log('PacketHandler: Poly', msg.payload.objId, msg.payload.polyId);
    this.emit('poly', msg.payload);
  }

  /** S_Invis (opcode 57) — 투명 on/off (invisType 0 = 해제). */
  private handleInvis(msg: { type: 'invis'; payload: InvisPayload }): void {
    console.log('PacketHandler: Invis', msg.payload.objId, msg.payload.invisType);
    this.emit('invis', msg.payload);
  }

  /** S_Light (opcode 53) — 광원 반경(타일) 갱신. */
  private handleLight(msg: { type: 'light'; payload: LightPayload }): void {
    console.log('PacketHandler: Light', msg.payload.objId, msg.payload.lightSize);
    this.emit('light', msg.payload);
  }

  /** S_OwnCharAttrDef (opcode 15) — AC/속성 방어력의 부분 갱신 (ac는 음수 가능). */
  private handleAttrDef(msg: { type: 'attr_def'; payload: AttrDefPayload }): void {
    console.log('PacketHandler: Attr def', msg.payload);
    this.emit('attr_def', msg.payload);
  }

  /** S_ChangeHeading (opcode 199) — 제자리 방향 전환 (heading 0~7, 위치는 그대로). */
  private handleChangeHeading(msg: { type: 'change_heading'; payload: ChangeHeadingPayload }): void {
    console.log('PacketHandler: Change heading', msg.payload.objId, msg.payload.heading);
    this.emit('change_heading', msg.payload);
  }

  /** S_ShopSellList (opcode 254) — NPC가 파는 물건 목록 = 플레이어의 "구매" 목록. */
  private handleShopSellList(msg: { type: 'shop_sell_list'; payload: ShopSellListPayload }): void {
    console.log('PacketHandler: Shop sell list', msg.payload.objId, msg.payload.items?.length ?? 0);
    this.emit('shop_sell_list', msg.payload);
  }

  /** S_ShopBuyList (opcode 170) — NPC가 사주는 값 목록 = 플레이어의 "판매" 목록. */
  private handleShopBuyList(msg: { type: 'shop_buy_list'; payload: ShopBuyListPayload }): void {
    console.log('PacketHandler: Shop buy list', msg.payload.objId, msg.payload.items?.length ?? 0);
    this.emit('shop_buy_list', msg.payload);
  }

  /** 창고 목록 — npc_action("retrieve")의 응답. warehouseType 3이 개인 창고다. */
  private handleRetrieveList(msg: { type: 'retrieve_list'; payload: RetrieveListPayload }): void {
    console.log(
      'PacketHandler: Retrieve list',
      msg.payload.objId,
      msg.payload.warehouseType,
      msg.payload.items?.length ?? 0
    );
    this.emit('retrieve_list', msg.payload);
  }

  /**
   * S_AttackPacket / S_AttackMissPacket (opcode 142) — 전투 공격 결과.
   *
   * payload의 `damage`는 서버가 언제나 1로 채우는 자리라서 피해량이 아니다.
   * 명중/빗나감 여부만 그대로 넘기고, 표시 방식은 GameScene이 정한다.
   */
  private handleAttackResult(msg: { type: 'attack_result'; payload: AttackResultPayload }): void {
    const p = msg.payload;
    if (typeof p?.attackerObjId !== 'number' || typeof p?.targetObjId !== 'number') return;
    this.emit('attack_result', p);
  }

  /** S_Poison (opcode 93) — 외형 이상상태 색상 (0=정상 1=독 2=마비). */
  private handlePoison(msg: { type: 'poison'; payload: PoisonPayload }): void {
    if (typeof msg.payload?.objId !== 'number') return;
    this.emit('poison', msg.payload);
  }

  /** S_SpMr (opcode 174) — SP/마법저항 갱신. */
  private handleSpMr(msg: { type: 'sp_mr'; payload: SpMrPayload }): void {
    if (typeof msg.payload?.sp !== 'number') return;
    this.emit('sp_mr', msg.payload);
  }

  /** S_Strup (120) / S_Dexup (28) — 능력치 상승. */
  private handleStatUp(msg: { type: 'stat_up'; payload: StatUpPayload }): void {
    const p = msg.payload;
    if (!p || typeof p.value !== 'number') return;
    console.log('PacketHandler: Stat up', p.stat, p.value);
    this.emit('stat_up', p);
  }

  /** S_CharTitle (opcode 202) — 캐릭터 칭호. 빈 문자열은 해제다. */
  private handleCharTitle(msg: { type: 'char_title'; payload: CharTitlePayload }): void {
    if (typeof msg.payload?.objId !== 'number') return;
    this.emit('char_title', msg.payload);
  }

  /** S_Door (opcode 35) — 문 타일의 통행 가능 여부 변경. */
  private handleDoorStatus(msg: { type: 'door_status'; payload: DoorStatusPayload }): void {
    const p = msg.payload;
    if (typeof p?.x !== 'number' || typeof p?.y !== 'number') return;
    this.emit('door_status', p);
  }

  /**
   * S_CommonNews — 로그인 직후 공지.
   *
   * 게이트웨이가 원본 페이로드를 그대로 넘기므로 형태가 고정되어 있지 않다.
   * 문자열을 건질 수 있을 때만 표시하고, 아니면 조용히 버린다.
   */
  private handleCommonNews(msg: { type: 'common_news'; payload: unknown }): void {
    const text = PacketHandler.extractNewsText(msg.payload);
    if (!text) return;
    this.emit('common_news', { text });
  }

  /** S_Weather — 날씨 코드 (0 = 맑음). */
  private handleWeather(msg: { type: 'weather'; payload: number }): void {
    const code = typeof msg.payload === 'number' ? msg.payload : Number(msg.payload);
    if (!Number.isFinite(code)) return;
    this.emit('weather', { code });
  }

  /**
   * 공지 페이로드에서 사람이 읽을 문자열을 뽑는다.
   *
   * 서버 설정에 따라 문자열 하나로도, 문자열 배열로도, `{ message }` 객체로도
   * 올 수 있어 세 형태를 모두 받는다.
   */
  private static extractNewsText(payload: unknown): string {
    if (typeof payload === 'string') return payload.trim();
    if (Array.isArray(payload)) {
      return payload.filter((v) => typeof v === 'string').join('\n').trim();
    }
    if (payload && typeof payload === 'object') {
      const obj = payload as Record<string, unknown>;
      for (const key of ['message', 'text', 'news', 'html']) {
        if (typeof obj[key] === 'string') return (obj[key] as string).trim();
      }
    }
    return '';
  }

  /** S_HpUpdate (opcode 42) — HP 갱신. mp_update와 대칭. */
  private handleHpUpdate(msg: { type: 'hp_update'; payload: HpUpdatePayload }): void {
    const currentHp = msg.payload?.currentHp;
    if (typeof currentHp !== 'number') return;
    if (typeof msg.payload.maxHp === 'number') this.lastMaxHp = msg.payload.maxHp;
    this.emit('hp:update', { currentHp, maxHp: this.lastMaxHp });
  }

  /**
   * S_MpUpdate (opcode 73) — MP 갱신.
   *
   * payload가 정수에서 `{ currentMp, maxMp }` 객체로 바뀌었다.
   * 재빌드 전 구버전 게이트웨이가 붙어 있을 수 있으므로 정수도 그대로 받는다
   * (정수면 currentMp만 갱신하고 maxMp는 마지막으로 알던 값을 유지).
   */
  private handleMpUpdate(msg: { type: 'mp_update'; payload: MpUpdatePayload | number }): void {
    const raw = msg.payload;
    const currentMp = typeof raw === 'number' ? raw : raw?.currentMp;
    if (typeof currentMp !== 'number') return;

    if (typeof raw !== 'number' && typeof raw?.maxMp === 'number') {
      this.lastMaxMp = raw.maxMp;
    }
    this.emit('mp:update', { currentMp, maxMp: this.lastMaxMp });
  }

  /**
   * S_BookmarkLoad / S_Bookmarks — 순간이동 북마크 목록.
   * 로그인/맵 이동 시점에 다시 올 수 있으므로 받은 목록으로 통째로 교체한다.
   */
  private handleBookmarks(msg: { type: 'bookmarks'; payload: BookmarkEntry[] }): void {
    const list = Array.isArray(msg.payload) ? msg.payload : [];
    this.bookmarks = [...list];
    console.log('PacketHandler: Bookmarks', this.bookmarks.length);
    this.emit('bookmarks', [...this.bookmarks]);
  }

  /** S_IdentifyDesc — 확인 주문서 감정 결과 (문장 번호 + 인자). */
  private handleIdentifyDesc(
    msg: { type: 'identify_desc'; payload: IdentifyDescPayload }
  ): void {
    console.log('PacketHandler: Identify desc', msg.payload.messageId, msg.payload.args);
    this.emit('identify_desc', msg.payload);
  }

  // ========== Event System ==========

  /**
   * Subscribe to game events.
   */
  on(event: string, callback: (data: unknown) => void): void {
    if (!this.gameListeners.has(event)) {
      this.gameListeners.set(event, new Set());
    }
    this.gameListeners.get(event)!.add(callback);
  }

  /**
   * Unsubscribe from game events.
   */
  off(event: string, callback: (data: unknown) => void): void {
    this.gameListeners.get(event)?.delete(callback);
  }

  /**
   * Emit event to listeners.
   */
  private emit(event: string, data: unknown): void {
    this.gameListeners.get(event)?.forEach((callback) => callback(data));
  }

  /**
   * Remove all listeners.
   */
  clearListeners(): void {
    this.gameListeners.clear();
  }

  // ========== Getters for cached data ==========

  /**
   * Get cached character list.
   */
  getCharList(): CharInfo[] {
    return [...this.charList];
  }

  /**
   * 캐릭터 목록이 확정됐는지. 빈 목록이 "캐릭터 없음"인지 "아직 안 받음"인지
   * 가리는 유일한 근거다 — 씬이 리스너를 걸기 전에 지나간 목록을 캐시로 되짚을 때 쓴다.
   */
  isCharListResolved(): boolean {
    return this.charListResolved;
  }

  /**
   * Get cached character count.
   */
  getCharCount(): number {
    return this.charCount;
  }

  /**
   * Get player spawn data.
   */
  getPlayerSpawn(): CharSpawnPayload | null {
    return this.playerSpawn ? { ...this.playerSpawn } : null;
  }

  /**
   * 캐시된 순간이동 북마크 목록.
   * 상태의 진실은 GameState.getBookmarks()이며, 여기 사본은 편의용이다.
   */
  getBookmarks(): BookmarkEntry[] {
    return [...this.bookmarks];
  }

  /**
   * Clear cached data (on disconnect / new login session).
   */
  clearCache(): void {
    this.cancelCharListFallback();
    this.charList = [];
    this.charCount = 0;
    this.charListResolved = false;
    this.playerSpawn = null;
    // 북마크는 캐릭터별 데이터라 세션이 바뀌면 반드시 비운다.
    this.bookmarks = [];
    this.lastMaxHp = 0;
    this.lastMaxMp = 0;
  }

  // ========== Utility Methods ==========

  /**
   * Calculate heading from movement direction.
   * Heading is 0-7 representing 8 directions.
   */
  static calculateHeading(dx: number, dy: number): number {
    // Convert dx/dy to heading (0-7, where 0 = east, going counter-clockwise)
    // 0: east (+x)
    // 1: northeast
    // 2: north (-y)
    // 3: northwest
    // 4: west (-x)
    // 5: southwest
    // 6: south (+y)
    // 7: southeast

    if (dx > 0 && dy === 0) return 0; // east
    if (dx > 0 && dy < 0) return 1; // northeast
    if (dx === 0 && dy < 0) return 2; // north
    if (dx < 0 && dy < 0) return 3; // northwest
    if (dx < 0 && dy === 0) return 4; // west
    if (dx < 0 && dy > 0) return 5; // southwest
    if (dx === 0 && dy > 0) return 6; // south
    if (dx > 0 && dy > 0) return 7; // southeast

    return 0;
  }

  /**
   * 채널명 → C_Chat(opcode 190)의 type 값.
   *
   * 게이트웨이 `ProtocolTranslator.getChatType()`과 같은 표여야 한다.
   * **'whisper'는 여기 없다** — 귓속말 송신은 C_Chat이 아니라 별도 패킷이라
   * (서버 `C_Whisper`) 게이트웨이에도 아직 경로가 없다. 넘기면 normal로 떨어진다.
   * 수신 쪽 채널명 해석은 위 `serverChatChannelName()`이 담당한다.
   */
  static getChatType(channel: string): number {
    const lower = channel.toLowerCase();
    switch (lower) {
      case 'normal':
        return CHAT_TYPE.NORMAL;
      case 'shout':
        return CHAT_TYPE.SHOUT;
      case 'world':
        return CHAT_TYPE.WORLD;
      case 'clan':
        return CHAT_TYPE.CLAN;
      case 'party':
        return CHAT_TYPE.PARTY;
      case 'trade':
        return CHAT_TYPE.TRADE;
      case 'alliance':
        return CHAT_TYPE.ALLIANCE;
      default:
        return CHAT_TYPE.NORMAL;
    }
  }
}
